import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QuestionMarkCircleIcon, XMarkIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useHelpCentre } from '../../contexts/HelpCentreContext';
import HelpCentre from './HelpCentre';

export const HELP_ROLES = ['academyAdmin', 'salesRep', 'coach', 'superAdmin'];

// Topbar "Help" button and the slide-over panel it opens — in both the CRM
// and Onboarding portals (they share PageShell/Topbar). The panel stays open
// while staff follow a "Take me there" link, so they can keep the steps up
// beside the page; it closes itself only on the Help page, which shows the
// same thing full-size.
export default function HelpButton() {
  const { t } = useTranslation();
  const { role } = useAuth();
  const help = useHelpCentre();
  const { pathname } = useLocation();
  const onHelpPage = pathname === '/admin/help';

  useEffect(() => {
    if (onHelpPage && help?.open) help.setOpen(false);
  }, [onHelpPage]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!help || !HELP_ROLES.includes(role)) return null;
  const { open, setOpen } = help;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title={t('admin:help.button', 'Help')}
        aria-label={t('admin:help.button', 'Help')}
        className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium hover:bg-[var(--bg)]"
        style={{ color: open ? 'var(--accent)' : 'var(--text-2, #475569)' }}
      >
        <QuestionMarkCircleIcon style={{ width: 20, height: 20 }} />
        <span className="hidden sm:inline">{t('admin:help.button', 'Help')}</span>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[1002] bg-black/20 sm:hidden" onClick={() => setOpen(false)} />
          <aside
            className="fixed z-[1003] bg-white shadow-2xl flex flex-col inset-x-0 bottom-0 top-16 rounded-t-2xl sm:rounded-none sm:inset-y-0 sm:top-0 sm:left-auto sm:right-0 sm:w-[420px] sm:border-l sm:border-gray-200"
            role="dialog"
            aria-label={t('admin:help.panelTitle', 'Help Centre')}
          >
            <div className="flex items-center justify-between px-4 pt-4 pb-2 flex-shrink-0">
              <h2 className="text-base font-semibold text-gray-900">{t('admin:help.panelTitle', 'Help Centre')}</h2>
              <div className="flex items-center gap-1">
                {role !== 'superAdmin' && (
                  <Link to="/admin/help" title={t('admin:help.openFull', 'Open full page')} className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                    <ArrowsPointingOutIcon className="h-4 w-4" />
                  </Link>
                )}
                <button type="button" onClick={() => setOpen(false)} className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 px-4 pb-4">
              <HelpCentre />
            </div>
          </aside>
        </>
      )}
    </>
  );
}
